import { PermissionModifier } from '@/shared/constants/permissions/PermissionModifier'
import { PermissionScope } from '@/shared/constants/permissions/PermissionScope'
import { PERMISSION_DELIMITER } from '@/shared/constants/permissions/PermissionDelimiter'
import { LinkType } from '@/shared/constants/permissions/LinkType'
import { PermissionLevel } from '@/shared/constants/permissions/PermissionLevel'
import { cloneDeep } from 'lodash'
import { OperationReturnType } from '@/shared/constants/permissions/OperationReturnType'
import { PermissionRequirement } from '@/shared/constants/permissions/PermissionRequirement'

/**
 * Transforms permission text into an object
 * @param {string} perm Unmodified permission string
 * @param {Object|null} allPerms Any previously transformed permissions object
 * @returns
 */
export const transformPermissions = (perm, allPerms = {}) => {
  let splitStr = ''
  let modifier = ''
  let key = ''

  // 1. Split permission string by delimiter
  splitStr = perm.split(PERMISSION_DELIMITER)

  // 2. Validate size of split string (should contain 2 parts, key and perm modifier)
  if (splitStr.length !== 2) return null

  // 3. Extract permission parts
  key = splitStr[0]
  modifier = splitStr[1]

  const permObj =
    allPerms && Object.prototype.hasOwnProperty.call(allPerms, key)
      ? allPerms[key]
      : {
          view: false,
          modify: false,
          delete: false,
          create: false,
        }

  // 4. Assign permissions to transformed perm object
  if (modifier !== PermissionModifier.ALL) {
    permObj[modifier] = true
  } else {
    // Iterate over all available permissions and set to true
    for (const property in permObj) {
      permObj[property] = true
    }
  }

  // 5. Return transformed perm object in expected format (e.g. { account: { view: true, ...}})
  return { [key]: permObj }
}

/**
 * Iterates over permissions payload and transforms each node into a permissions object
 * @param {Array} permissions Permissions payload
 * @returns
 */
export const processPermissionsPayload = (permissions) => {
  const perms = cloneDeep(permissions)
  let transformedPerms
  for (const group of perms) {
    // Process group permission
    transformedPerms = {}
    if (group.access.permissions.length > 0) {
      for (const perm of group.access.permissions) {
        transformedPerms = {
          ...transformedPerms,
          ...transformPermissions(perm, transformedPerms),
        }
      }
    }
    group.access.permissions = transformedPerms

    // Process client permissions
    for (const client of group.clients) {
      transformedPerms = {}
      if (client.access.permissions.length > 0) {
        for (const perm of client.access.permissions) {
          transformedPerms = {
            ...transformedPerms,
            ...transformPermissions(perm, transformedPerms),
          }
        }
      }
      client.access.permissions = transformedPerms

      // Process location permissions
      for (const location of client.locations) {
        transformedPerms = {}
        if (location.access.permissions.length > 0) {
          for (const perm of location.access.permissions) {
            transformedPerms = {
              ...transformedPerms,
              ...transformPermissions(perm, transformedPerms),
            }
          }
        }
        location.access.permissions = transformedPerms
      }
    }
  }

  return perms
}

/**
 * Iterates of permissions/access tree at a specified level, returning a specific item or places those items in an array by checking some condition
 * @param {Array} permissions Entire permissions tree
 * @param {LinkType} level GROUP|CLIENT|LOCATION
 * @param {OperationReturnType} returnType Object or Array type. Return a list of items (Array) or return a single item (Object)
 * @param {Function} comparer Compares the current item against some condition. Needs to return true or false.
 * @returns
 */
export const iterateOverPermissionsTree = (
  permissions,
  level,
  returnType,
  comparer = () => {
    return true
  }
) => {
  if (!Object.values(OperationReturnType).includes(returnType))
    throw TypeError(
      `Return type must be list or single. Received: ${returnType}`
    )

  if (!permissions || permissions.length === 0)
    throw Error(`Permissions tree is empty`)

  const list = []
  for (const group of permissions) {
    if (level === LinkType.GROUP) {
      if (comparer(group)) {
        if (returnType === OperationReturnType.LIST) list.push(group)
        else if (returnType === OperationReturnType.SINGLE) return group
      }
      continue
    }

    for (const client of group.clients) {
      if (level === LinkType.CLIENT) {
        if (comparer(client)) {
          if (returnType === OperationReturnType.LIST) list.push(client)
          else if (returnType === OperationReturnType.SINGLE) return client
        }
        continue
      }

      for (const location of client.locations) {
        if (level === LinkType.LOCATION) {
          if (comparer(location)) {
            if (returnType === OperationReturnType.LIST) list.push(location)
            else if (returnType === OperationReturnType.SINGLE) return location
          }
          continue
        }
      }
    }
  }

  return returnType === OperationReturnType.LIST ? list : null
}

/**
 * Returns the appropriate id key name based on level
 * @param {LinkType} level
 */
export const getNodeIdKeyBasedOnLevel = (level) => {
  // Validate level
  if (!Object.values(LinkType).includes(level))
    throw TypeError(`Invalid level supplied. Received: ${level}`)

  return `${level.toLowerCase()}Id`
}

/**
 * Returns a permissions node by Id from a specified level
 * @param {Array} permissions Entire permissions tree
 * @param {Number} nodeId Id of requested node (e.g. locationId)
 * @param {LinkType} level
 * @returns
 */
export const getNodeById = (permissions, nodeId, level) => {
  return iterateOverPermissionsTree(
    permissions,
    level,
    OperationReturnType.SINGLE,
    (item) => {
      return item[getNodeIdKeyBasedOnLevel(level)] === nodeId
    }
  )
}

/**
 * Returns Boolean indicating if user has permission
 * @param {Array} permissionTree
 * @param {Number} nodeId
 * @param {LinkType} level
 * @param {PermissionScope} scope
 * @param {PermissionModifier} modifier
 * @returns Boolean
 */
export const getNodePermission = (
  permissionTree,
  nodeId,
  level,
  scope,
  modifier
) => {
  const node = getNodeById(permissionTree, nodeId, level)
  if (!node) return false

  // Validate scope
  if (!Object.values(PermissionScope).includes(scope))
    throw TypeError(`Invalid scope supplied. Received: ${scope}`)

  // If node doesn't have the scope, user doesn't have permissions within that scope
  if (!Object.prototype.hasOwnProperty.call(node.access.permissions, scope))
    return false

  if (modifier !== PermissionModifier.ALL)
    return node.access.permissions[scope][modifier]

  for (const scopedPermission in node.access.permissions[scope]) {
    if (!node.access.permissions[scope][scopedPermission]) return false
  }

  return true
}

/**
 * Retrieves all permission nodes at a given level
 * @param {Array} permissions
 * @param {LinkType} level
 * @returns
 */
export const getAllNodesByLevel = (permissions, level) => {
  return iterateOverPermissionsTree(
    permissions,
    level,
    OperationReturnType.LIST
  )
}

/**
 * Counts all permission nodes at a given level
 * @param {Array} permissions
 * @param {LinkType} level
 * @returns
 */
export const countAllNodesByLevel = (permissions, level) => {
  return iterateOverPermissionsTree(
    permissions,
    level,
    OperationReturnType.LIST
  ).length
}

/**
 * Returns true if the user has the provided permissions scope at any level
 * @param {Array} permissions
 * @param {PermissionScope} scope
 * @returns Boolean
 */
export const hasPermissionAtAnyLevel = (permissions, scope, modifier) => {
  for (const level in LinkType) {
    const result = iterateOverPermissionsTree(
      permissions,
      level.toLowerCase(),
      OperationReturnType.SINGLE,
      (item) => {
        return !!item.access.permissions[scope]?.[modifier]
      }
    )

    if (result) return true
  }

  return false
}

const convertPermissionLevelToLinkType = (permissionLevel) => {
  switch (permissionLevel) {
    case null:
    case PermissionLevel.GROUP:
    case PermissionLevel.GROUP_AND_BELOW:
      return LinkType.GROUP
    case PermissionLevel.CLIENT:
    case PermissionLevel.CLIENT_AND_BELOW:
    case PermissionLevel.SELECTED_CLIENT_AND_BELOW:
      return LinkType.CLIENT
    case PermissionLevel.LOCATION:
      return LinkType.LOCATION
    default:
      throw new Error('Invalid permission level')
  }
}

/**
 * Determines if a user has access to a given route
 * @param {Object} routeTo
 * @param {Vuex} store
 * @param {Number} targetId Id of entity in permissions tree to target
 * @returns Boolean
 */
export const hasAccessToRoute = (routeTo, store, targetId = -1) => {
  // No permissions set for route, no action needed
  if (!routeTo?.meta?.permissions || routeTo.meta.permissions.list.length === 0)
    return true

  let hasPermission = false

  for (const permission of routeTo.meta.permissions.list) {
    switch (permission.level) {
      case null:
      case PermissionLevel.GROUP_AND_BELOW:
        hasPermission = store.getters['auth/hasPermissionRegardlessOfLevel'](
          permission.scope,
          permission.modifier
        )
        break
      case PermissionLevel.GROUP:
      case PermissionLevel.CLIENT:
      case PermissionLevel.LOCATION:
        hasPermission = store.getters['auth/getPermission'](
          targetId,
          convertPermissionLevelToLinkType(permission.level),
          permission.scope,
          permission.modifier
        )
        break
      case PermissionLevel.CLIENT_AND_BELOW:
        hasPermission = store.getters[
          'auth/hasPermissionForAtleastOneClientLocation'
        ](targetId, permission.scope, permission.modifier)
        break
      case PermissionLevel.SELECTED_CLIENT_AND_BELOW:
        hasPermission = store.getters[
          'auth/hasPermissionForAtleastOneClientLocation'
        ](
          store.getters['client/getSelectedClients'],
          permission.scope,
          permission.modifier
        )
        break
      default:
        throw new Error('Invalid permission level')
    }

    // Only one permission required
    if (
      routeTo.meta.permissions.requirement === PermissionRequirement.ONE &&
      hasPermission
    )
      return true

    // All permissions in list are required
    if (
      routeTo.meta.permissions.requirement === PermissionRequirement.ALL &&
      !hasPermission
    )
      return false
  }

  // At this point, if require one permission, return false as user didn't have
  // at least one permission. If require all, return true as user had all perms
  return routeTo.meta.permissions.requirement === PermissionRequirement.ALL
}

export const flattenAccessTree = (permissions) => {
  if (!permissions || permissions.length === 0)
    throw Error(`Permissions tree is empty`)

  const list = []
  for (const group of permissions) {
    for (const client of group.clients) {
      list.push({
        locations: client.locations,
        numLocations: client.locations.length,
        clientId: client.clientId,
        clientName: client.clientName,
        clientGroupId: group.groupId,
        clientGroupName: group.groupName,
      })
    }
  }

  return list
}

export const flattenPermissionsWithLocations = (permissions) => {
  if (!permissions || permissions.length === 0)
    throw Error(`Permissions tree is empty`)

  const list = []
  for (const group of permissions) {
    for (const client of group.clients) {
      for (const location of client.locations) {
        list.push({
          locationId: location.locationId,
          locationName: location.locationName,
          clientId: client.clientId,
          clientName: client.clientName,
          clientGroupId: group.groupId,
          clientGroupName: group.groupName,
        })
      }
    }
  }

  return list
}
